// Gedeelde gegevens: wie is aangemeld, en de planningsdata (elke minuut ververst). Een wijziging aan een taak staat
// meteen op het scherm, met Ongedaan maken; de server bevestigt ze op de achtergrond (outbox.ts), en Planner verwerkt
// ze daarna zelf in 15 s tot 2 min.
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { api } from "./api";
import { applyDeps, enqueue, overlay, snapshot, subscribe } from "./outbox";
import { inverseOps } from "./planning";
import type { Fase, NewTask, OpsResult, PlanningData, Task, TaskOp } from "./types";
import { useToast } from "../shell/Toast";

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 5 * 60e3 });
}

/** De planningsdata, met de wijzigingen die nog onderweg zijn naar de server erover. */
export function usePlanning() {
  const sending = useSyncExternalStore(subscribe, snapshot);
  const select = useCallback((d: PlanningData) => overlay(d, sending), [sending]);
  return useQuery({ queryKey: ["planning"], queryFn: () => api.data(), refetchInterval: 60e3, staleTime: 30e3, select });
}

export function useProposals() {
  return useQuery({ queryKey: ["proposals"], queryFn: api.proposals, refetchInterval: 60e3 });
}

/** Opzoektabellen bij de planningsdata. */
export function useIndex(data: PlanningData | undefined) {
  return useMemo(() => {
    const people = new Map((data?.people || []).map((p) => [p.id, p]));
    const klanten = new Map((data?.klanten || []).map((k) => [k.id, k]));
    const buckets = new Map((data?.buckets || []).map((b) => [b.id, b]));
    const fases = new Map((data?.fases || []).map((f) => [f.id, f]));
    const tasks = new Map((data?.tasks || []).map((t) => [t.id, t]));
    return { people, klanten, buckets, fases, tasks };
  }, [data]);
}

/**
 * Het antwoord van de server in de gedeelde data: de taak zoals de server ze nu kent, en de afhankelijkheden.
 * Een verversing die al liep, las de data nog zonder deze wijziging: die vervangen door een nieuwe.
 */
/**
 * De taak zoals de server ze teruggeeft in de gedeelde data. Een afgewerkte taak (done) die weer open is, verhuist naar
 * tasks; een open taak die net afgewerkt is, blijft in tasks tot de volgende lezing (isOpen filtert ze weg).
 */
function putTask(d: PlanningData, task: Task): PlanningData {
  // een fase (samenvattende taak, fasevenster in Taken) hoort in fases, nooit tussen de taken
  if ((task as unknown as Fase).fase) {
    const f = task as unknown as Fase;
    return { ...d, fases: d.fases.map((x) => (x.id === f.id ? { ...x, ...f, parentId: f.parentId ?? (task.faseId ?? null) } : x)) };
  }
  const done = (d.done || []).filter((t) => t.id !== task.id);
  if (task.progress >= 1 && !d.tasks.some((t) => t.id === task.id)) return { ...d, done: [...done, { ...task, done: true }] };
  const t2 = { ...task, done: false };
  return d.tasks.some((t) => t.id === task.id) ? { ...d, tasks: d.tasks.map((t) => (t.id === task.id ? t2 : t)), done } : { ...d, tasks: [...d.tasks, t2], done };
}

function commit(qc: QueryClient, taskId: string, ops: TaskOp[], r: OpsResult) {
  qc.setQueryData<PlanningData>(["planning"], (d) => (d ? { ...putTask(d, r.task), deps: applyDeps(d.deps, [{ taskId, ops }]) } : d));
  if (qc.isFetching({ queryKey: ["planning"] })) qc.invalidateQueries({ queryKey: ["planning"] });
}

/** Resultaat van een wijziging of Ongedaan maken in de gedeelde data: taak vervangen, toevoegen of weghalen. */
function applyResult(qc: ReturnType<typeof useQueryClient>, r: OpsResult) {
  qc.setQueryData<PlanningData>(["planning"], (d) => {
    if (!d) return d;
    if (r.deleted) return { ...d, tasks: d.tasks.filter((t) => t.id !== r.task.id) };
    return putTask(d, r.task);
  });
}

/** Nieuwe taak: meteen op het bord, met Ongedaan maken (dat de taak weer verwijdert). */
export function useCreateTask() {
  const qc = useQueryClient();
  const toast = useToast();
  const undo = useMutation({
    mutationFn: (changeId: number) => api.undo(changeId),
    onSuccess: (r) => { applyResult(qc, r); toast.flash("Ongedaan gemaakt: de taak is weer weg"); },
    onError: (e: Error) => toast.flash(e.message, { error: true }),
  });
  const m = useMutation({
    mutationFn: ({ task }: { task: NewTask; message: string }) => api.createTask(task),
    onSuccess: (r, v) => {
      applyResult(qc, r);
      const id = r.changeId;
      // de taak staat in Planner, maar de Clockify-taak met schatting lukte niet: zeggen, met Ongedaan maken
      toast.flash(r.warning ? r.warning : v.message, { ...(id ? { undo: () => undo.mutate(id) } : {}), error: !!r.warning });
    },
    onError: (e: Error) => toast.flash(e.message, { error: true }),
  });
  return { run: (task: NewTask, message: string) => m.mutateAsync({ task, message }), busy: m.isPending };
}

const why = (e: unknown) => (e instanceof Error ? e.message : "er ging iets mis");

/**
 * Wijzigingen aan taken: meteen op het scherm, en `message` meteen in de melding, met Ongedaan maken. Mislukt het bij
 * de server, dan staat de taak weer zoals voorheen en zegt een melding waarom.
 * run geeft het antwoord van de server terug (of de fout), voor wie daarna iets wil verversen.
 */
export function useTaskOps() {
  const qc = useQueryClient();
  const toast = useToast();
  const send = (task: Task, ops: TaskOp[], call: () => Promise<OpsResult>) => enqueue(task.id, ops, call, (r) => commit(qc, task.id, ops, r));
  // Ongedaan maken: meteen terug op het scherm; de server draait de wijziging terug zodra hij ze aanvaard heeft.
  // Mislukte de wijziging zelf, dan is er niets terug te draaien (en geen fout om te melden).
  const revert = (task: Task, ops: TaskOp[], sent: Promise<OpsResult>) =>
    send(task, inverseOps(task, ops), () => sent.then((r) => (r.changeId ? api.undo(r.changeId) : r)))
      .then(() => {}, (e) => sent.then(() => { throw e; }, () => {}));

  const run = (task: Task, ops: TaskOp[], message: string) => {
    const sent = send(task, ops, () => api.ops(task.id, ops));
    toast.flash(message, {
      undo: () => {
        toast.flash("Ongedaan gemaakt");
        revert(task, ops, sent).catch((e) => toast.flash("Ongedaan maken mislukt: " + why(e), { error: true }));
      },
    });
    sent.catch((e) => toast.flash("Niet opgeslagen: " + why(e), { error: true }));
    return sent;
  };

  // Een bestand: de server zet het in SharePoint en hangt het dan als bijlage aan de taak. In de wachtrij van de taak
  // (na de vorige wijziging; de volgende wacht erop), zonder wijziging om alvast te tonen: de lade toont de voortgang
  // zelf, en het adres kent enkel de server. Ongedaan maken haalt de bijlage weg; het bestand blijft in SharePoint.
  const upload = (task: Task, file: File, onProgress?: (frac: number) => void) => {
    const sent = enqueue<OpsResult>(task.id, [], () => api.upload(task.id, file, onProgress), (r) => commit(qc, task.id, [], r));
    sent.then((r) => {
      const f = r.file;
      const ops: TaskOp[] = f ? [{ kind: "link", url: f.url, name: f.name, on: true, file: true, type: f.type }] : [];
      toast.flash("Bestand toegevoegd aan " + task.title + ": " + (f?.name || file.name), ops.length ? {
        undo: () => {
          toast.flash("Ongedaan gemaakt");
          revert(task, ops, sent).catch((e) => toast.flash("Ongedaan maken mislukt: " + why(e), { error: true }));
        },
      } : {});
    }, (e) => toast.flash("Niet opgeslagen: " + why(e), { error: true }));
    return sent;
  };

  // meerdere taken tegelijk (bv. Alle afsluiten): één melding, Ongedaan maken draait alles terug
  const runMany = async (items: { task: Task; ops: TaskOp[] }[], message: string) => {
    const sent = items.map((i) => send(i.task, i.ops, () => api.ops(i.task.id, i.ops)));
    toast.flash(message, {
      undo: async () => {
        toast.flash("Ongedaan gemaakt");
        const rs = await Promise.allSettled(items.map((i, k) => revert(i.task, i.ops, sent[k])));
        const bad = rs.filter((r) => r.status === "rejected").length;
        if (bad) toast.flash(bad === rs.length ? "Ongedaan maken mislukt" : "Ongedaan gemaakt · " + bad + " mislukt", { error: true });
      },
    });
    const rs = await Promise.allSettled(sent);
    const bad = rs.flatMap((r) => (r.status === "rejected" ? [r.reason] : []));
    if (bad.length) toast.flash((bad.length === rs.length ? "Niet opgeslagen: " : bad.length + " van " + rs.length + " niet opgeslagen: ") + why(bad[0]), { error: true });
  };
  return { run, runMany, upload };
}
